import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import '../AuthModal/AuthModal.scss';

const MailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2"/><path d="M2 7l10 7 10-7"/>
  </svg>
);

const LockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
  </svg>
);

const extractError = (err) => {
  if (!err) return 'Неизвестная ошибка';
  if (typeof err === 'string') return err;
  if (err.detail) return String(err.detail);
  if (err.message) return String(err.message);
  const flat = Object.values(err).flat();
  return flat.length ? String(flat[0]) : JSON.stringify(err);
};

export default function ResetPasswordModal({ open, onClose }) {
  const [step, setStep] = useState('email'); // 'email', 'code', 'password', 'success'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const response = await fetch('https://security.ncore.kg/api/account/password-reset/request/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || data.detail || 'Ошибка запроса кода');
      }
      
      setStep('code');
    } catch (err) {
      setError(extractError(err.message || err));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const response = await fetch('https://security.ncore.kg/api/account/password-reset/verify/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || data.detail || 'Неверный код');
      }
      
      setStep('password');
    } catch (err) {
      setError(extractError(err.message || err));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    
    if (newPassword !== confirmPassword) {
      setError('Пароли не совпадают');
      return;
    }
    
    if (newPassword.length < 8) {
      setError('Пароль должен содержать минимум 8 символов');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch('https://security.ncore.kg/api/account/password-reset/confirm/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email, 
          code,
          new_password: newPassword 
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || data.detail || 'Ошибка сброса пароля');
      }
      
      // Auto-login if tokens received
      if (data.access && data.refresh) {
        localStorage.setItem('token', data.access);
        localStorage.setItem('access_token', data.access);
        localStorage.setItem('refresh_token', data.refresh);
        if (data.user) {
          localStorage.setItem('auth_user', JSON.stringify(data.user));
        }
        
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
      
      setStep('success');
    } catch (err) {
      setError(extractError(err.message || err));
    } finally {
      setLoading(false);
    }
  };

  const modalContent = (
    <div className="auth-modal-overlay" onClick={onClose}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <div className="auth-modal-header">
          <h2 className="auth-modal-title">
            {step === 'email' && 'Восстановление пароля'}
            {step === 'code' && 'Введите код'}
            {step === 'password' && 'Новый пароль'}
            {step === 'success' && 'Готово!'}
          </h2>
          <button className="auth-close-btn" onClick={onClose}>×</button>
        </div>

        {error && <div className="af-error">{error}</div>}

        {step === 'email' && (
          <form onSubmit={handleRequestCode}>
            <p style={{ marginBottom: '20px', color: '#9F9FA0', fontSize: '14px' }}>
              Введите email или username, на который зарегистрирован аккаунт
            </p>
            <div className="af-group">
              <label className="af-label">Email или Username</label>
              <div className="af-field">
                <span className="af-icon"><MailIcon /></span>
                <span className="af-sep"></span>
                <input 
                  className="af-input" 
                  value={email} 
                  onChange={(e) => setEmail(e.target.value)} 
                  required 
                  placeholder="Введите email или username" 
                />
              </div>
            </div>
            <button className="af-submit-btn" type="submit" disabled={loading}>
              {loading ? 'Отправка...' : 'Отправить код'}
            </button>
          </form>
        )}

        {step === 'code' && (
          <form onSubmit={handleVerifyCode}>
            <p style={{ marginBottom: '20px', color: '#9F9FA0', fontSize: '14px' }}>
              Мы отправили 6-значный код на <strong style={{ color: '#fff' }}>{email}</strong>
            </p>
            <div className="af-group">
              <label className="af-label">Код подтверждения</label>
              <div className="af-field">
                <span className="af-icon"><LockIcon /></span>
                <span className="af-sep"></span>
                <input 
                  className="af-input" 
                  value={code} 
                  onChange={(e) => setCode(e.target.value)} 
                  required 
                  placeholder="     "
                  maxLength={6}
                  style={{ letterSpacing: '4px', textAlign: 'center' }}
                  autoFocus
                />
              </div>
            </div>
            <button className="af-submit-btn" type="submit" disabled={loading}>
              {loading ? 'Проверка...' : 'Подтвердить'}
            </button>
            <div style={{ textAlign: 'center', marginTop: '12px' }}>
              <button 
                className="af-link-btn" 
                onClick={() => setStep('email')} 
                type="button"
              >
                Изменить email
              </button>
            </div>
          </form>
        )}

        {step === 'password' && (
          <form onSubmit={handleResetPassword}>
            <p style={{ marginBottom: '20px', color: '#9F9FA0', fontSize: '14px' }}>
              Придумайте новый пароль для вашего аккаунта
            </p>
            <div className="af-group">
              <label className="af-label">Новый пароль</label>
              <div className="af-field">
                <span className="af-icon"><LockIcon /></span>
                <span className="af-sep"></span>
                <input 
                  className="af-input" 
                  type="password"
                  value={newPassword} 
                  onChange={(e) => setNewPassword(e.target.value)} 
                  required 
                  placeholder="Минимум 8 символов" 
                />
              </div>
            </div>
            <div className="af-group">
              <label className="af-label">Повторите пароль</label>
              <div className="af-field">
                <span className="af-icon"><LockIcon /></span>
                <span className="af-sep"></span>
                <input 
                  className="af-input" 
                  type="password"
                  value={confirmPassword} 
                  onChange={(e) => setConfirmPassword(e.target.value)} 
                  required 
                  placeholder="Повторите пароль" 
                />
              </div>
            </div>
            <button className="af-submit-btn" type="submit" disabled={loading}>
              {loading ? 'Сохранение...' : 'Сменить пароль'}
            </button>
          </form>
        )}

        {step === 'success' && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>✅</div>
            <p style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px', color: '#fff' }}>
              Пароль успешно изменен!
            </p>
            <p style={{ color: '#9F9FA0', marginBottom: '24px' }}>
              Вы будете автоматически авторизованы
            </p>
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}
